import React, { FC, useRef } from 'react';
import { Form as FormikForm, Formik, FormikHelpers, FormikProps, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import * as paths from 'paths';
import { useNotify } from 'hooks';
import {
  GenericStatus,
  LeaderboardConfig,
  LeaderboardEventType,
  useCreateUpdateLeaderboardConfigMutation,
  useModifyLeaderboardScoresMutation,
} from 'api';

import { useUpdateLeaderboard } from 'features/admin/updaters';
import { LeaderboardSchema } from 'features/admin/validations';
import { LeaderboardForm } from 'features/admin/components/Leaderboards/Partials';
import { flattenDeep, values } from 'lodash';

const LeaderboardsCreate: FC = () => {
  const { t } = useTranslation();
  const notify = useNotify();
  const navigate = useNavigate();

  const mutationOptions = useUpdateLeaderboard();
  const { mutateAsync: createAsyncLeaderboardConfig } = useCreateUpdateLeaderboardConfigMutation({
    onError: notify.mutationError,
    ...mutationOptions,
  });

  const { mutate: updateLeaderboardScores } = useModifyLeaderboardScoresMutation({
    onError: notify.mutationError,
    onSuccess,
  });

  const initialFormikRef = useFormik({
    onSubmit: () => undefined,
    initialValues: {
      id: '',
      created_at: 0,
      segment_ids: [],
      title: '',
      description: '',
      leaderboard_status: GenericStatus.Disabled,
      all_users: false,
      scoring_aggregate: {
        everyonesocial: {
          posting: [
            { score: '0', event: LeaderboardEventType.ShareablePost },
            { score: '0', event: LeaderboardEventType.ShareablePhotoPost },
            { score: '0', event: LeaderboardEventType.ShareableBrandedPost },
            { score: '0', event: LeaderboardEventType.PhotoPost },
            { score: '0', event: LeaderboardEventType.Post },
          ],
          actions: [
            { score: '0', event: LeaderboardEventType.ProfilePhoto },
            { score: '0', event: LeaderboardEventType.Comment },
            { score: '0', event: LeaderboardEventType.Reaction },
            { score: '0', event: LeaderboardEventType.UserMention },
            { score: '0', event: LeaderboardEventType.CommentReaction },
            { score: '0', event: LeaderboardEventType.FollowUser },
            { score: '0', event: LeaderboardEventType.InvitationSent },
            { score: '0', event: LeaderboardEventType.GroupCreated },
            { score: '0', event: LeaderboardEventType.FollowGroup },
          ],
          teammates: [
            { score: '0', event: LeaderboardEventType.InvitationAccepted },
            { score: '0', event: LeaderboardEventType.ReceivedShare },
            { score: '0', event: LeaderboardEventType.ReceivedComment },
            { score: '0', event: LeaderboardEventType.ReceivedReaction },
            { score: '0', event: LeaderboardEventType.ReceivedCommentReaction },
            { score: '0', event: LeaderboardEventType.Followed },
          ],
        },
        linkedin: {
          general: [
            { score: '0', event: LeaderboardEventType.LinkedinConnected },
            { score: '0', event: LeaderboardEventType.BrandedLinkedinShare },
            { score: '0', event: LeaderboardEventType.LinkedinShare },
            { score: '0', event: LeaderboardEventType.BrandedLinkedinClick },
            { score: '0', event: LeaderboardEventType.LinkedinClick },
            { score: '0', event: LeaderboardEventType.BrandedLinkedinEngagement },
            { score: '0', event: LeaderboardEventType.LinkedinEngagement },
            { score: '0', event: LeaderboardEventType.BrandedLinkedinLike },
            { score: '0', event: LeaderboardEventType.NonBrandedLinkedinLike },
            { score: '0', event: LeaderboardEventType.BrandedLinkedinComment },
            { score: '0', event: LeaderboardEventType.NonBrandedLinkedinComment },
          ],
        },
        twitter: {
          general: [
            { score: '0', event: LeaderboardEventType.TwitterConnected },
            { score: '0', event: LeaderboardEventType.BrandedTwitterShare },
            { score: '0', event: LeaderboardEventType.TwitterShare },
            { score: '0', event: LeaderboardEventType.BrandedTwitterClick },
            { score: '0', event: LeaderboardEventType.TwitterClick },
            { score: '0', event: LeaderboardEventType.BrandedTwitterEngagement },
            { score: '0', event: LeaderboardEventType.TwitterEngagement },
            { score: '0', event: LeaderboardEventType.BrandedTwitterRetweet },
            { score: '0', event: LeaderboardEventType.NonBrandedTwitterRetweet },
            { score: '0', event: LeaderboardEventType.BrandedTwitterLike },
            { score: '0', event: LeaderboardEventType.NonBrandedTwitterLike },
          ],
        },
        facebook: {
          general: [
            { score: '0', event: LeaderboardEventType.BrandedFacebookShare },
            { score: '0', event: LeaderboardEventType.FacebookShare },
            { score: '0', event: LeaderboardEventType.BrandedFacebookClick },
            { score: '0', event: LeaderboardEventType.FacebookClick },
          ],
        },
      },
    },
  });

  const formikRef = useRef<FormikProps<LeaderboardConfig>>(initialFormikRef);

  function getScores() {
    const events = values(formikRef.current.values.scoring_aggregate);
    const scores = events.map(event => values(event));
    return flattenDeep(scores);
  }

  function onSuccess() {
    notify.info({ message: t('components:savedSuccessfully') });
    navigate(paths.adminLeaderboards);
  }

  async function handleSubmit(
    values: LeaderboardConfig,
    formikHelpers: FormikHelpers<LeaderboardConfig>
  ) {
    try {
      // TODO: remove when LeaderboardConfigInput can handle scoring_aggregate
      const scores = getScores();

      const submitValues = { ...values };

      delete submitValues.created_at;
      delete submitValues.scoring_aggregate;

      await createAsyncLeaderboardConfig({ data: submitValues }).then(data => {
        updateLeaderboardScores({
          id: data.createUpdateLeaderboardConfig.id,
          scores,
        });
      });
    } catch (e) {
      formikHelpers.setSubmitting(false);
      notify.mutationError();
    }
  }

  return (
    <Formik
      initialValues={formikRef.current.initialValues}
      innerRef={ref => (formikRef.current = ref)}
      onSubmit={handleSubmit}
      validationSchema={LeaderboardSchema(true)}
    >
      <FormikForm>
        <LeaderboardForm />
      </FormikForm>
    </Formik>
  );
};

export default LeaderboardsCreate;
